import PropTypes from 'prop-types';
import Button from 'Components/Atoms/Button/Button';
import FormProvider, {
    RHFSelect,
    RHFInput,
    RHFCheckbox,
    RHFTextarea,
    RHFSelectProduct,
} from 'Components/Organisms/HookForm';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { PAYMENT_METHODS_TYPES } from 'Utils/constants';
import Divider from 'Components/Atoms/Divider/Divider';

export default function InvoicePaymentMethodEditor(props) {
    const { onCancel, paymentMethod, onSubmit, onError } = props;

    const { t } = useTranslation('invoices');

    const serieSchema = yup.object().shape({
        name: yup.string().required(t('field-required')),
        type: yup.string().oneOf(Object.values(PAYMENT_METHODS_TYPES)).required(t('field-required')),
        description: yup.string().max(512, t('max-length-512-characters')).notRequired(),
        product_id: yup.string().notRequired(),
    });

    const methods = useForm({
        resolver: yupResolver(serieSchema),
        defaultValues: {
            name: paymentMethod.name ?? '',
            type: paymentMethod.type ?? PAYMENT_METHODS_TYPES.CASH,
            description: paymentMethod.description ?? '',
            product_id: paymentMethod.product_id ?? '',
            is_default: paymentMethod.is_default ?? false,
        },
    });

    const {
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    const typeOptions = useMemo(
        () => [
            { value: PAYMENT_METHODS_TYPES.CASH, label: t('cash') },
            { value: PAYMENT_METHODS_TYPES.BANK_DIRECT_DEBIT, label: t('direct-debit') },
            { value: PAYMENT_METHODS_TYPES.BANK_TRANSFER, label: t('bank-transfer') },
            { value: PAYMENT_METHODS_TYPES.CREDIT_CARD, label: t('credit-card') },
            { value: PAYMENT_METHODS_TYPES.FACTORING, label: t('factoring') },
            { value: PAYMENT_METHODS_TYPES.PAYPAL, label: t('paypal') },
            { value: PAYMENT_METHODS_TYPES.BIZUM, label: t('bizum') },
            { value: PAYMENT_METHODS_TYPES.CHECK, label: t('check') },
            { value: PAYMENT_METHODS_TYPES.OTHER, label: t('other') },
        ],
        [t],
    );

    const submit = async (data) => {
        const normalizedBody = {
            ...data,
            description: data.description.trim() === '' ? null : data.description.trim(),
            product_id: data.product_id === '' ? null : data.product_id,
        };

        try {
            await onSubmit(normalizedBody);
        } catch (error) {
            onError(error);
        }
    };

    return (
        <FormProvider className="flex flex-col h-full" methods={methods} onSubmit={handleSubmit(submit)}>
            <div className="grow pb-6 pt-6">
                <div className="grid gap-5">
                    <RHFInput className="w-full" name="name" data-autofocus="true" label={t('name')} />
                    <RHFSelect name="type" className="w-full" label={t('payment-method-type')} options={typeOptions} />
                    <RHFTextarea
                        className="w-full"
                        name="description"
                        label={t('payment-instructions')}
                        rows={3}
                        placeholder={t('payment-instructions-placeholder')}
                        maxLength={512}
                    />
                    <Divider />
                    <RHFSelectProduct className="w-full" name="product_id" label={t('product')} />
                    <RHFCheckbox className="text-sm" name="is_default">
                        {t('default-payment-method')}
                    </RHFCheckbox>
                </div>
            </div>

            <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                <div className="flex gap-2">
                    <Button variant="primary" isBusy={isSubmitting} type="submit" className="grow">
                        {t('save')}
                    </Button>
                    <Button className="grow" onClick={onCancel}>
                        {t('cancel')}
                    </Button>
                </div>
            </div>
        </FormProvider>
    );
}

InvoicePaymentMethodEditor.propTypes = {
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    paymentMethod: PropTypes.object.isRequired,
};
